import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, MessageCircle, User, Phone, Sparkles, Check } from 'lucide-react';
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const EVENT_TYPES = ['Boda', 'Quinceañera', 'Graduación', 'Cumpleaños', 'Corporativo', 'Otro'];
function startOfMonth(year: number, month: number) {
  return new Date(year, month, 1);
}
function daysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function isPast(date: Date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}
export function BookingCalendar() {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [eventType, setEventType] = useState(EVENT_TYPES[0]);
  const calendarCells = useMemo(() => {
    const firstDay = startOfMonth(viewYear, viewMonth).getDay();
    const total = daysInMonth(viewYear, viewMonth);
    const cells: (Date | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(viewYear, viewMonth, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewYear, viewMonth]);
  const goPrev = () => {
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };
  const goNext = () => {
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };
  const formattedDate = selectedDate ? selectedDate.toLocaleDateString('es-MX', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';
  const smsBody = selectedDate ? `Hola Celebrate Events! Soy ${name || '[tu nombre]'}. Me gustaría reservar el ${formattedDate} para mi ${eventType}. Mi teléfono: ${phone || '[tu teléfono]'}.` : '';
  const smsLink = `sms:+14049409785?body=${encodeURIComponent(smsBody)}`;
  const waLink = `https://wa.me/14049409785?text=${encodeURIComponent(smsBody)}`;
  const canSubmit = selectedDate && name.trim() && phone.trim();
  return <section id="reservar" className="py-20 md:py-28 bg-ink relative overflow-hidden" data-id="element-16" data-mp="3hzfpl3tgfstl">
      {/* Ambient gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-[40%] bg-gold/5 blur-[60px] pointer-events-none" data-id="element-17" data-mp="1ba4axkewwamc"></div>

      <div className="container mx-auto px-4 md:px-6 max-w-6xl relative z-10" data-id="element-18" data-mp="16cc99yogta4y">
        <div className="text-center mb-12" data-id="element-19" data-mp="1l0p7l2b0z52m">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/30 rounded-full text-gold text-sm font-medium mb-6" data-id="element-20" data-mp="1l1qlrfnc87ok">
            <CalendarIcon className="w-4 h-4" data-id="element-21" data-mp="1u0c9rqbkwewq" />
            <span data-id="element-22" data-mp="1tz0uat8rtips">Disponibilidad en tiempo real</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-cream mb-4" data-id="element-23" data-mp="1p0qycgf82e34">
            Reserva la fecha de tu celebración
          </h2>
          <p className="font-script text-2xl md:text-3xl text-gold" data-id="element-24" data-mp="23wyqq40salth">
            Las fechas más bonitas se agotan rápido
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-25" data-mp="9k8q73fwued4">
          {/* Calendar */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7
        }} className="bg-charcoal rounded-2xl p-6 md:p-8 border border-gold/20 shadow-2xl" data-id="element-26" data-mp="4mz1xr49c8qv">
            {/* Month header */}
            <div className="flex items-center justify-between mb-6" data-id="element-27" data-mp="294y9hdp36e82">
              <button onClick={goPrev} aria-label="Mes anterior" className="w-10 h-10 rounded-full border border-gold/30 text-gold hover:bg-gold/10 transition-colors flex items-center justify-center" data-id="element-28" data-mp="2e2qbj0jg3gpz">
                <ChevronLeft className="w-5 h-5" data-id="element-29" data-mp="2mwl99ni8m4vl" />
              </button>
              <div className="text-center" data-id="element-30" data-mp="1qcm7is2cheyg">
                <div className="font-serif text-xl md:text-2xl font-bold text-cream" data-id="element-31" data-mp="1qbktceq18cci">
                  {MONTHS[viewMonth]} {viewYear}
                </div>
              </div>
              <button onClick={goNext} aria-label="Mes siguiente" className="w-10 h-10 rounded-full border border-gold/30 text-gold hover:bg-gold/10 transition-colors flex items-center justify-center" data-id="element-32" data-mp="1muopp9w9wbk8">
                <ChevronRight className="w-5 h-5" data-id="element-33" data-mp="18ung961xzvt5" />
              </button>
            </div>

            {/* Day labels */}
            <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-34" data-mp="13y6qk7f3h9dy">
              {DAYS.map(d => <div key={d} className="text-center text-xs font-medium text-gold/70 uppercase tracking-wide py-2" data-id="element-35" data-mp="z4cbxiecjklq">
                  {d}
                </div>)}
            </div>

            {/* Calendar cells */}
            <div className="grid grid-cols-7 gap-1" data-id="element-36" data-mp="l52w4ju1ysia">
              {calendarCells.map((cell, idx) => {
              if (!cell) return <div key={idx} className="aspect-square" data-id="element-37" data-mp="g7t9fby1suy5" />;
              const past = isPast(cell);
              const isToday = isSameDay(cell, today);
              const isSelected = selectedDate && isSameDay(cell, selectedDate);
              return <button key={idx} disabled={past} onClick={() => setSelectedDate(cell)} className={`aspect-square rounded-lg text-sm font-medium transition-all relative
                      ${past ? 'text-cream/20 cursor-not-allowed' : 'text-cream hover:bg-gold/10 cursor-pointer'}
                      ${isToday && !isSelected ? 'border border-gold/40' : ''}
                      ${isSelected ? 'bg-gold text-ink font-bold shadow-[0_0_15px_rgba(212,175,55,0.5)]' : ''}
                    `} data-id="element-38" data-mp="3srf7gw9ll0ec" data-mpi={idx}>
                    {cell.getDate()}
                  </button>;
            })}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-white/5 text-xs text-cream/60" data-id="element-39" data-mp="1d0sope6wcahh">
              <div className="flex items-center gap-2" data-id="element-40" data-mp="2sqtk9xer1ck2">
                <div className="w-3 h-3 rounded border border-gold/40" data-id="element-41" data-mp="2sqacvamq66ka"></div>
                <span data-id="element-42" data-mp="37fzjhldl72ry">Hoy</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-43" data-mp="37hixf5brt1qk">
                <div className="w-3 h-3 rounded bg-gold" data-id="element-44" data-mp="32ho9nanaks9q"></div>
                <span data-id="element-45" data-mp="2eqsi8emqzz30">Fecha elegida</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-46" data-mp="2eramnyjkdjm0">
                <div className="w-3 h-3 rounded bg-cream/10" data-id="element-47" data-mp="29sr1i0csobup"></div>
                <span data-id="element-48" data-mp="2nrqpd5shgmpt">No disponible</span>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} className="bg-charcoal rounded-2xl p-6 md:p-8 border border-gold/20 shadow-2xl flex flex-col" data-id="element-49" data-mp="2jplwwadrtw4r">
            <div className="mb-6" data-id="element-50" data-mp="22gjbjf17ngax">
              <h3 className="font-serif text-2xl text-cream mb-2" data-id="element-51" data-mp="22fi0vbo3wvtq">
                Cuéntanos de tu evento
              </h3>
              <p className="text-cream/60 text-sm font-light" data-id="element-52" data-mp="279ckkf3ppast">
                Llena los datos y te confirmamos la disponibilidad por mensaje.
              </p>
            </div>

            {/* Selected date display */}
            <AnimatePresence mode="wait" data-id="element-53" data-mp="2l7kcg8lw5lx4">
              {selectedDate ? <motion.div key="selected" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className="bg-gold/10 border border-gold/30 rounded-xl p-4 mb-6 flex items-center gap-3" data-id="element-54" data-mp="2l5ga3v3n4uyy">
                  <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center shrink-0" data-id="element-55" data-mp="309st6lhztzh9">
                    <Check className="w-5 h-5 text-ink" data-id="element-56" data-mp="2vc0pku7zkox8" />
                  </div>
                  <div data-id="element-57" data-mp="3s18b428luy15">
                    <div className="text-xs text-gold/80 uppercase tracking-wide font-medium" data-id="element-58" data-mp="3s0gq1lcmwip4">
                      Fecha seleccionada
                    </div>
                    <div className="text-cream font-medium capitalize" data-id="element-59" data-mp="3j0tngw8656uh">
                      {formattedDate}
                    </div>
                  </div>
                </motion.div> : <motion.div key="empty" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="border border-dashed border-cream/20 rounded-xl p-4 mb-6 flex items-center gap-3 text-cream/50" data-id="element-60" data-mp="27ldg44if9r8k">
                  <Sparkles className="w-5 h-5" data-id="element-61" data-mp="2iry3yp05egqn" />
                  <span className="text-sm" data-id="element-62" data-mp="2dzy9hc2ukg1g">
                    Selecciona una fecha del calendario
                  </span>
                </motion.div>}
            </AnimatePresence>

            {/* Form fields */}
            <div className="space-y-4 flex-1" data-id="element-63" data-mp="2mqykg0v5l7ah">
              <div data-id="element-64" data-mp="2mucm16vztcgv">
                <label className="block text-xs font-medium text-cream/70 uppercase tracking-wide mb-2" data-id="element-65" data-mp="31oz9caxm6iv4">
                  Tu nombre
                </label>
                <div className="relative" data-id="element-66" data-mp="2wqxrtufu6rlr">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40" data-id="element-67" data-mp="17ayb9s9w1zw3" />
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Ej. María Gómez" className="w-full bg-ink border border-white/10 rounded-lg pl-10 pr-4 py-3 text-cream placeholder-cream/30 focus:border-gold focus:outline-none transition-colors" data-id="element-68" data-mp="12cesg0qld47y" />
                </div>
              </div>

              <div data-id="element-69" data-mp="39779m58o0553">
                <label className="block text-xs font-medium text-cream/70 uppercase tracking-wide mb-2" data-id="element-70" data-mp="396p6qprhyso7">
                  Teléfono
                </label>
                <div className="relative" data-id="element-71" data-mp="2zbncxgma12fa">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40" data-id="element-72" data-mp="2z89bcalfsx8w" />
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(404) 555-0123" className="w-full bg-ink border border-white/10 rounded-lg pl-10 pr-4 py-3 text-cream placeholder-cream/30 focus:border-gold focus:outline-none transition-colors" data-id="element-73" data-mp="2ld5u9xb8ag48" />
                </div>
              </div>

              <div data-id="element-74" data-mp="i19b2tw52ima">
                <label className="block text-xs font-medium text-cream/70 uppercase tracking-wide mb-2" data-id="element-75" data-mp="i0psrc2kts7q">
                  Tipo de evento
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-76" data-mp="84mq853tbrk9">
                  {EVENT_TYPES.map(type => <button key={type} type="button" onClick={() => setEventType(type)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${eventType === type ? 'bg-gold text-ink border-gold shadow-[0_0_12px_rgba(212,175,55,0.4)]' : 'bg-transparent text-cream/80 border-white/10 hover:border-gold/50'}`} data-id="element-77" data-mp="15s4yzfxh4to9">
                      {type}
                    </button>)}
                </div>
              </div>
            </div>

            {/* Submit buttons */}
            <div className="mt-8 space-y-3" data-id="element-78" data-mp="1s1c0g071627s">
              <a href={canSubmit ? waLink : '#'} target="_blank" rel="noopener noreferrer" onClick={e => {
              if (!canSubmit) {
                e.preventDefault();
              }
            }} className={`flex items-center justify-center gap-2 px-6 py-4 rounded-full font-bold text-lg transition-all ${canSubmit ? 'bg-gold hover:bg-gold-light text-ink hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] hover:-translate-y-0.5' : 'bg-cream/10 text-cream/40 cursor-not-allowed'}`} data-id="element-79" data-mp="1s2n5028avdzy">
                <MessageCircle className="w-5 h-5" data-id="element-80" data-mp="2jtsyhztimdg9" />
                <span data-id="element-81" data-mp="2opi1kd4quchb">Reservar por WhatsApp</span>
              </a>

              <a href={canSubmit ? smsLink : '#'} onClick={e => {
              if (!canSubmit) {
                e.preventDefault();
              }
            }} className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full font-medium border-2 transition-all ${canSubmit ? 'border-gold text-gold hover:bg-gold/10' : 'border-cream/10 text-cream/30 cursor-not-allowed'}`} data-id="element-82" data-mp="2tpwby1tthyii">
                <Phone className="w-4 h-4" data-id="element-83" data-mp="92m7b7uvnf9l" />
                <span data-id="element-84" data-mp="416n1i0ixcs9">Enviar SMS</span>
              </a>

              {!canSubmit && <p className="text-xs text-cream/50 text-center pt-2" data-id="element-85" data-mp="3cgqc04u5w91i">
                  Completa la fecha, nombre y teléfono para continuar
                </p>}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}